'use client'

import React, { useContext, useMemo } from 'react'
import { ChevronDown, Search, X } from 'lucide-react'

import { useSession, useTenant } from '@rewardops/auth'
import {
  cn,
  LocalizedLink,
  useFavouritingAndWishlistingStore,
  useFilter,
} from '@rewardops/ui'

import { filterNavLinks } from '~/utils/filter-utils'
import { getSearchDropdownOptions, SearchTypes } from '~/utils/sort-utils'
import { useIsClient } from '~/components/hooks/use-is-client'
import { useLocaleChange } from '~/components/hooks/use-locale-change'
import { useNavigationLinks } from '~/components/hooks/use-navigation-links'
import { useSearchBox } from '~/components/hooks/use-search-box'
import { MapleSelect } from '~/components/maple/components/select'
import MapleLogo from '~/components/maple/maple-logo'
import { CategoryGrid } from '~/components/maple/navbar/category-grid'
import {
  HeartLink,
  ShoppingCartLink,
} from '~/components/maple/navbar/link-icons'
import { PointsBalance } from '~/components/maple/navbar/points-balance'
import { styling } from '~/components/styling'
import { useTranslation } from '~/app/i18n/client'
import { useCart } from '~/context/cart-context'
import { ElpPromotionsContext } from '~/context/elp-promotions-context'
import { PromotionsContext } from '~/context/promotions-context'
import { ResourcesContext } from '~/context/resources-provider'
import { DesktopMenuLink } from './desktop-menu-link'
import { ProfileMenu } from './profile-dropdown-menu'
import { SearchBar } from './search-bar'
import { SearchOverlay } from './search-overlay'

export function DesktopMainNav({
  categories,
  isLoading,
}: {
  categories: any
  isLoading: boolean
}) {
  const { t } = useTranslation()
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const { data: session } = useSession()
  const tenant = useTenant()
  const retailersEnabled = tenant?.flags?.retailers
  const { getCartQuantity } = useCart()
  const resources = useContext(ResourcesContext)

  const { searchQuery, searchType } = useFilter()

  const { promotionsLoading, totalItems } = useContext(PromotionsContext)
  const { showPromotionsLink } = useContext(ElpPromotionsContext)

  const {
    isSearchBarOpened,
    openSearchBar,
    debouncedState,
    updateSearchQuery,
    clearSearchInput,
    resetSearch,
  } = useSearchBox()

  const searchOverlay = useMemo(() => {
    if (!searchQuery || !searchType) return null
    return <SearchOverlay />
  }, [searchQuery, searchType])

  const { savedItemIds } = useFavouritingAndWishlistingStore((state) => state)

  const cartQuantity = getCartQuantity()

  const fontClass =
    tenant.styleVariant?.header?.links?.weight || 'font-semibold'

  const handleMouseEnter = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
    }
    setIsDropdownOpen(true)
  }

  // Add some debounce to the dropdown close
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false)
    }, 250)
  }

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const showSpecialOffers =
    !promotionsLoading && totalItems > 0 && tenant?.flags.specialOffersPage

  const isShopAndEarnEnabled =
    !resources?.navigationMenu?.omitLinks?.includes('retailers') &&
    !resources?.isNavigationMenuLoading

  const isSpecialOffersEnabled =
    !resources?.navigationMenu?.omitLinks?.includes('special-offers') &&
    !resources?.isNavigationMenuLoading

  const { directLinks, menuLinks } = useNavigationLinks({
    direct: resources?.navigationMenu?.directLinks,
    menu: resources?.navigationMenu?.menuLinks,
  })

  return (
    <>
      <div className="border-b bg-nav-color text-nav-text-color">
        <div className="container flex flex-row items-center justify-between gap-6 p-5 lg:max-w-[1250px]">
          <MapleLogo />
          {isSearchBarOpened ? (
            <>
              <div className="relative w-1/2">
                <SearchBar
                  selector={
                    <MapleSelect
                      id={`search-type-selector`}
                      className="w-auto rounded-r-md border bg-gray-100 px-1 pl-2 pr-1 text-sm"
                      selectItemClassName="font-normal text-grey-300"
                      placeholder="Select search type"
                      defaultValue={searchType || SearchTypes.Products}
                      options={getSearchDropdownOptions(t)}
                      onValueChange={(option) =>
                        updateSearchQuery({ query: searchQuery, qType: option })
                      }
                      withBorder={true}
                    />
                  }
                  onChange={updateSearchQuery}
                  onClose={() => clearSearchInput()}
                  searchQuery={debouncedState.query}
                  selectorDisabled={!retailersEnabled}
                />
              </div>
            </>
          ) : (
            <>
              {tenant?.flags?.categories && (
                <div
                  id="nav-bar-category-grid"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    id="nav-bar-shop-categories-button"
                    className={cn(
                      `inline-flex gap-1 text-sm`,
                      isDropdownOpen && 'text-tenant-primary-color',
                      fontClass,
                      tenant?.customStyling?.navLinkTwo
                        ? styling.LinkTwo
                        : styling.Link,
                    )}
                  >
                    {t('nav.shopCategories')}
                    <ChevronDown className="h-6 w-6" />
                  </button>
                  {isDropdownOpen && !isLoading && (
                    <div className="absolute left-0 right-0 top-full z-10 w-screen bg-white text-black shadow-md">
                      <div className="custom-scrollbar max-h-[80vh] overflow-y-auto">
                        <div className="container mx-auto lg:max-w-[1250px]">
                          <LocalizedLink
                            id={`main-category-view-all`}
                            href={`/category/all`}
                            onClick={() => setIsDropdownOpen(false)}
                            className={cn(
                              'ml-4 mr-12 mt-4 inline-block text-xl font-bold uppercase text-gray-800',
                              styling.Link,
                            )}
                          >
                            {t('general.viewAll')}
                          </LocalizedLink>
                          <CategoryGrid
                            categories={categories}
                            setIsDropdownOpen={setIsDropdownOpen}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {tenant?.flags?.earnRewardsPage && (
                <>
                  <LocalizedLink
                    href="/earn-rewards"
                    id="earn-rewards-link"
                    className={cn(
                      'inline-flex gap-1 text-sm',
                      tenant?.customStyling?.navLinkTwo
                        ? styling.LinkTwo
                        : styling.Link,
                    )}
                  >
                    {t('nav.earnRewards')}
                  </LocalizedLink>
                  <LocalizedLink
                    href="/travel"
                    id="travel-discounts-link"
                    className={cn(
                      'inline-flex gap-1 text-sm',
                      tenant?.customStyling?.navLinkTwo
                        ? styling.LinkTwo
                        : styling.Link,
                    )}
                  >
                    {t('nav.travelAdvisorDiscounts')}
                  </LocalizedLink>
                </>
              )}
              {tenant?.flags?.bookingMadeEasy && (
                <LocalizedLink
                  href="/booking-made-easy"
                  id="booking-made-easy-link"
                  className={cn(
                    'inline-flex gap-1 text-sm',
                    tenant?.customStyling?.navLinkTwo
                      ? styling.LinkTwo
                      : styling.Link,
                  )}
                >
                  {t('nav.bookingMadeEasy')}
                </LocalizedLink>
              )}

              {showSpecialOffers && isSpecialOffersEnabled && (
                <LocalizedLink
                  href="/special-offers"
                  id="deals-link"
                  className={cn(
                    'inline-flex gap-1 text-sm',
                    tenant?.customStyling?.navLinkTwo
                      ? styling.LinkTwo
                      : styling.Link,
                  )}
                >
                  {t('nav.specialOffers')}
                </LocalizedLink>
              )}
              {tenant?.flags?.retailers && isShopAndEarnEnabled && (
                <LocalizedLink
                  href="/retailers?sort=name_ascending"
                  id="shop-and-earn-link"
                  className={cn(
                    'inline-flex gap-1 text-sm',
                    tenant?.customStyling?.navLinkTwo
                      ? styling.LinkTwo
                      : styling.Link,
                  )}
                >
                  {t('nav.shopAndEarn')}
                </LocalizedLink>
              )}
              {tenant?.flags?.payAnywhere && (
                <LocalizedLink
                  href="/pay-anywhere"
                  id="pay-anywhere-link"
                  className={cn(
                    'inline-flex gap-1 text-sm',
                    tenant?.customStyling?.navLinkTwo
                      ? styling.LinkTwo
                      : styling.Link,
                  )}
                >
                  Pay Anywhere
                </LocalizedLink>
              )}

              {/* contentful navigation menu */}
              {filterNavLinks(
                directLinks,
                tenant?.theme,
                showPromotionsLink,
              )?.map((directLink) => (
                <LocalizedLink
                  key={directLink?.id}
                  href={directLink?.to}
                  id={directLink?.id}
                  className={cn(
                    'inline-flex gap-1 text-sm',
                    tenant?.customStyling?.navLinkTwo
                      ? styling.LinkTwo
                      : styling.Link,
                  )}
                >
                  {directLink?.name}
                </LocalizedLink>
              ))}

              {menuLinks?.map((menu) => (
                <DesktopMenuLink key={menu?.id} menu={menu as any} />
              ))}

              <div className="ml-auto flex items-center gap-6">
                {session?.user?.member && (
                  <PointsBalance
                    member={session?.user?.member}
                    color={
                      tenant?.customStyling?.navLinkTwo ? 'white' : undefined
                    }
                  />
                )}
                {tenant?.flags?.searchEnabled && (
                  <button
                    id="search"
                    className={cn(
                      'hover:text-tenant-primary-color',
                      tenant?.customStyling?.navLinkTwo && 'hover:text-white',
                    )}
                    aria-label={t('nav.search')}
                    onClick={() => openSearchBar()}
                  >
                    <Search />
                  </button>
                )}
                {session?.user ? (
                  tenant?.flags?.wishlist && (
                    <HeartLink
                      savedItemsCount={savedItemIds?.size ?? 0}
                      className={
                        tenant?.customStyling?.navLinkTwo
                          ? 'hover:text-white'
                          : undefined
                      }
                    />
                  )
                ) : (
                  <ProfileMenu
                    color={
                      tenant?.customStyling?.navLinkTwo ? 'white' : undefined
                    }
                  />
                )}
                {tenant?.flags?.shoppingCart && (
                  <ShoppingCartLink
                    cartQuantity={cartQuantity}
                    className={
                      tenant?.customStyling?.navLinkTwo
                        ? 'hover:text-white'
                        : undefined
                    }
                  />
                )}
                <div>
                  <LanguageSwitcher />
                </div>
              </div>
            </>
          )}
          {isSearchBarOpened ? (
            <button
              onClick={() => resetSearch()}
              className="opacity-70 transition-opacity hover:opacity-100"
            >
              <X
                className={cn(
                  'h-6 w-6 hover:text-tenant-primary-color',
                  tenant?.customStyling?.navLinkTwo && 'hover:text-white',
                )}
              />
            </button>
          ) : null}
        </div>
      </div>
      {/* Full-page search overlay */}
      {searchOverlay}
    </>
  )
}

const LanguageSwitcher = () => {
  const isClient = useIsClient()
  const tenant = useTenant()
  const { currentLanguage, availableLanguages, changeLocale } =
    useLocaleChange()

  // skip rendering on the server to avoid hydration issues
  // since the locale is not available at that time
  if (!isClient || availableLanguages?.length <= 1) return null

  const defaultOption = availableLanguages.find(
    (lang) => lang.key === currentLanguage,
  )

  const fontClass =
    tenant.styleVariant?.header?.links?.weight || 'font-semibold'

  return (
    <MapleSelect
      id="language-selector"
      className={cn(
        'text-sm hover:text-tenant-primary-color',
        fontClass,
        tenant?.customStyling?.navLinkTwo && 'hover:text-white',
      )}
      placeholder={defaultOption?.value as any as any}
      defaultValue={defaultOption?.key}
      options={availableLanguages}
      onValueChange={(lang) => changeLocale(lang)}
    />
  )
}
